/**
 * @component QuillEditor
 * @description 문의 내용 입력을 위한 리치 텍스트 편집기를 React 제어 방식으로 감싼 공통 에디터 컴포넌트입니다.
 * @usage ContactFormPanel에서 문의 본문 입력 필드로 사용됩니다.
 */

import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'color', 'align'];

const getInlineFormatsForEnter = (formats) => {
  const nextFormats = {};
  ['bold', 'italic', 'underline', 'color'].forEach((key) => {
    if (formats[key]) {
      nextFormats[key] = formats[key];
    }
  });
  return nextFormats;
};

const QUILL_TOOLBAR_HTML = `
  <span class="ql-formats">
    <button type="button" class="ql-bold" aria-label="bold"></button>
    <button type="button" class="ql-italic" aria-label="italic"></button>
    <button type="button" class="ql-underline" aria-label="underline"></button>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-list" value="ordered" aria-label="ordered list"></button>
    <button type="button" class="ql-list" value="bullet" aria-label="bullet list"></button>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-link" aria-label="link"></button>
  </span>
  <span class="ql-formats">
    <select class="ql-color" aria-label="color"></select>
  </span>
  <span class="ql-formats">
    <select class="ql-align" aria-label="align"></select>
  </span>
  <span class="ql-formats">
    <button type="button" class="ql-clean" aria-label="clean"></button>
  </span>
`;

export default function QuillEditor({ value, onChange, placeholder }) {
  const toolbarRef = useRef(null);
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);

  useEffect(() => {
    // [콜백 동기화]: 최신 onChange 핸들러를 ref에 유지해 에디터 이벤트에서 안전하게 호출합니다.
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // [상태 동기화]: 외부 value prop을 ref에 저장해 초기화 시점에 참조합니다.
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (!hostRef.current || !toolbarRef.current || quillRef.current) return undefined;

    const hostElement = hostRef.current;
    const toolbarElement = toolbarRef.current;
    // Quill controls the toolbar DOM itself, so we hand it a stable element to avoid duplicate toolbars in Strict Mode.
    toolbarElement.innerHTML = QUILL_TOOLBAR_HTML;

    const editor = new Quill(hostElement, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: toolbarElement,
        },
        keyboard: {
          bindings: {
            preserveInlineEnter: {
              key: 13,
              shiftKey: false,
              collapsed: true,
              handler(range, context) {
                const format = context?.format || this.quill.getFormat(range.index, 0);
                const hasInlineStyle = !!(format.bold || format.italic || format.underline || format.color);
                const hasBlockStyle = !!(format.list || format.blockquote || format['code-block'] || format.header);

                if (!hasInlineStyle || hasBlockStyle) {
                  return true;
                }

                const nextFormats = getInlineFormatsForEnter(format);
                this.quill.insertText(range.index, '\n', nextFormats, 'user');
                this.quill.setSelection(range.index + 1, 0, 'silent');
                return false;
              },
            },
          },
        },
      },
      formats: QUILL_FORMATS,
      placeholder,
    });

    const handleTextChange = () => {
      // [상태 변경]: Quill 내부 HTML을 상위 상태로 전달해 폼과 동기화합니다.
      const nextHtml = editor.root.innerHTML === '<p><br></p>' ? '' : editor.root.innerHTML;
      onChangeRef.current(nextHtml);
    };

    quillRef.current = editor;
    if (valueRef.current) {
      editor.clipboard.dangerouslyPasteHTML(valueRef.current);
    }
    editor.on('text-change', handleTextChange);

    return () => {
      editor.off('text-change', handleTextChange);
      quillRef.current = null;
      toolbarElement.innerHTML = '';
      hostElement.innerHTML = '';
    };
  }, [placeholder]);

  useEffect(() => {
    const editor = quillRef.current;
    if (!editor) return;

    const currentHtml = editor.root.innerHTML === '<p><br></p>' ? '' : editor.root.innerHTML;
    if (value === currentHtml) return;

    // [편집 경험 보호]: 외부 value 반영 시 커서 위치를 보존해 타이핑 점프를 방지합니다.
    const selection = editor.getSelection();
    editor.clipboard.dangerouslyPasteHTML(value || '');
    if (selection) {
      editor.setSelection(selection);
    }
  }, [value]);

  return (
    <div className="quill-editor-shell">
      <div className="quill-editor-toolbar" ref={toolbarRef} />
      <div className="quill-editor-host" ref={hostRef} />
    </div>
  );
}
